import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useT } from '../../i18n';

const DAY_KEYS = ['hours_mon', 'hours_tue', 'hours_wed', 'hours_thu', 'hours_fri', 'hours_sat', 'hours_sun'];
const emptyDays = () => Object.fromEntries(DAY_KEYS.map((k) => [k, '']));
const weekTotal = (f) => DAY_KEYS.reduce((sum, k) => sum + (f[k] === '' || f[k] == null ? 0 : +f[k]), 0);
const hasDays = (f) => DAY_KEYS.some((k) => f[k] !== '' && f[k] != null);

export default function AdminSchedules() {
  const t = useT();
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', schedule_type: 'FIXED', user_id: '', start_time: '', end_time: '',
    daily_hours: 8, working_days_per_week: 5, break_after_hours: '', break_duration_minutes: '', is_default: false,
    ...emptyDays(),
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    const [sRes, uRes] = await Promise.all([api.get('/schedules/'), api.get('/users/')]);
    setSchedules(sRes.data);
    setUsers(uRes.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', schedule_type: 'FIXED', user_id: '', start_time: '', end_time: '', daily_hours: 8, working_days_per_week: 5, break_after_hours: '', break_duration_minutes: '', is_default: false, ...emptyDays() });
    setError('');
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, schedule_type: s.schedule_type, user_id: s.user_id || '',
      start_time: s.start_time || '', end_time: s.end_time || '',
      daily_hours: s.daily_hours, working_days_per_week: s.working_days_per_week,
      break_after_hours: s.break_after_hours ?? '', break_duration_minutes: s.break_duration_minutes ?? '',
      is_default: s.is_default,
      ...Object.fromEntries(DAY_KEYS.map((k) => [k, s[k] ?? ''])),
    });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        ...form,
        user_id: form.user_id ? +form.user_id : null,
        break_after_hours: form.break_after_hours !== '' ? +form.break_after_hours : null,
        break_duration_minutes: form.break_duration_minutes !== '' ? +form.break_duration_minutes : null,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        ...Object.fromEntries(DAY_KEYS.map((k) => [k, form[k] !== '' ? +form[k] : null])),
      };
      if (editing) {
        await api.put(`/schedules/${editing.id}`, payload);
      } else {
        await api.post('/schedules/', payload);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || t('admin.common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.schedules.confirmDelete'))) return;
    await api.delete(`/schedules/${id}`);
    await load();
  };

  const userName = (id) => {
    if (!id) return t('admin.schedules.defaultAll');
    const u = users.find((x) => x.id === id);
    return u ? `${u.first_name} ${u.last_name} (${u.personnel_number})` : t('admin.schedules.unknownUser', { id });
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0"><i className="bi bi-sliders me-2" />{t('admin.schedules.title')}</h4>
        <button className="btn btn-primary" onClick={openCreate}><i className="bi bi-plus me-2" />{t('admin.schedules.new')}</button>
      </div>

      {showForm && (
        <div className="card mb-4">
          <div className="card-body">
            <h6>{editing ? t('admin.schedules.edit') : t('admin.schedules.newTitle')}</h6>
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">{t('admin.schedules.name')}</label>
                  <input className="form-control" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">{t('admin.schedules.type')}</label>
                  <select className="form-select" value={form.schedule_type} onChange={(e) => setForm({ ...form, schedule_type: e.target.value })}>
                    <option value=                    "FIXED">{t('admin.schedules.typeFixedLong')}</option>
                                        <option value="FLEXTIME">{t('admin.schedules.typeFlex')}</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">{t('admin.schedules.employee')}</label>
                  <select className="form-select" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
                    <option value="">{t('admin.schedules.defaultAll')}</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.personnel_number})</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('admin.schedules.workStart')}</label>
                  <input type="time" className="form-control" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('admin.schedules.workEnd')}</label>
                  <input type="time" className="form-control" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('admin.schedules.dailyHours')}</label>
                  <input type="number" className="form-control" step="0.5" min="1" max="24" value={form.daily_hours}
                    onChange={(e) => setForm({ ...form, daily_hours: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('admin.schedules.daysPerWeek')}</label>
                  <input type="number" className="form-control" min="1" max="7" value={form.working_days_per_week}
                    onChange={(e) => setForm({ ...form, working_days_per_week: e.target.value })} />
                </div>
                <div className="col-md-4">
                  <label className=                  "form-label">{t('admin.schedules.autoBreakAfter')}</label>
                                    <input type="number" className="form-control" step="0.5" placeholder={t('admin.schedules.autoBreakAfterPlaceholder')} value={form.break_after_hours}
                    onChange={(e) => setForm({ ...form, break_after_hours: e.target.value })} />
                </div>
                <div className="col-md-4">
                  <label className=                  "form-label">{t('admin.schedules.autoBreakDuration')}</label>
                                    <input type="number" className="form-control" placeholder={t('admin.schedules.autoBreakDurationPlaceholder')} value={form.break_duration_minutes}
                    onChange={(e) => setForm({ ...form, break_duration_minutes: e.target.value })} />
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="isDefault" checked={form.is_default}
                      onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
                    <label className="form-check-label" htmlFor="isDefault">{t('admin.schedules.setDefault')}</label>
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label mb-1">{t('admin.schedules.dayHoursTitle')}</label>
                  <div className="row g-2">
                    {DAY_KEYS.map((k, i) => (
                      <div className="col" key={k}>
                        <label className="form-label small text-muted mb-0">{t(`calendar.weekday.${i + 1}`)}</label>
                        <input type="number" className="form-control" step="0.25" min="0" max="24" value={form[k]}
                          onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                      </div>
                    ))}
                  </div>
                  <div className="form-text">
                    {t('admin.schedules.dayHoursHint')}
                    {hasDays(form) && <strong className="ms-2">{t('admin.schedules.weekTotal', { hours: weekTotal(form) })}</strong>}
                  </div>
                </div>
              </div>
              {error &&  <div className="alert alert-danger mt-2 py-2">{error}</div>}
              <div className="mt-3 d-flex gap-2">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting && <span className="spinner-border spinner-border-sm me-2" />}{t('admin.common.save')}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowForm(false)}>{t('admin.common.cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? <div className="text-center"><div className="spinner-border" /></div> : (
        <div className="row g-3">
          {schedules.map((s) => (
            <div className="col-md-6 col-lg-4" key={s.id}>
              <div className={`card h-100 ${s.is_default ? 'border-primary' : ''}`}>
                <div className="card-header d-flex justify-content-between align-items-center">
                  <strong>{s.name}</strong>
                  {s.is_default && <span className="badge bg-primary">{t('admin.schedules.default')}</span>}
                </div>
                <div className="card-body small">
                  <div><span className="text-muted">{t('admin.schedules.typeLabel')}</span> {s.schedule_type === 'FIXED' ? t('admin.schedules.typeFixedShort') : t('admin.schedules.typeFlex')}</div>
                  <div><span className="text-muted">{t('admin.schedules.employeeLabel')}</span> {userName(s.user_id)}</div>
                  <div><span className="text-muted">{t('admin.schedules.targetLabel')}</span> {hasDays(s)
                    ? t('admin.schedules.weekTotal', { hours: s.weekly_target_hours })
                    : t('admin.schedules.targetValue', { hours: s.daily_hours, days: s.working_days_per_week })}</div>
                  {hasDays(s) && (
                    <div><span className="text-muted">{t('admin.schedules.dayHoursLabel')}</span>{' '}
                      {DAY_KEYS.map((k, i) => `${t(`calendar.weekday.${i + 1}`)} ${s[k] ?? 0}`).join(' · ')}</div>
                  )}
                  {s.start_time && <div><span className="text-muted">{t('admin.schedules.timesLabel')}</span> {s.start_time} – {s.end_time}</div>}
                  {s.break_after_hours && (
                    <div><span className="text-muted">{t('admin.schedules.autoBreakLabel')}</span> {t('admin.schedules.autoBreakValue', { hours: s.break_after_hours, minutes: s.break_duration_minutes })}</div>
                  )}
                </div>
                <div className="card-footer d-flex gap-2">
                  <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(s)}><i className="bi bi-pencil" /></button>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(s.id)}><i className="bi bi-trash" /></button>
                </div>
              </div>
            </div>
          ))}
          {schedules.length === 0 && <div className="text-muted">{t('admin.schedules.empty')}</div>}
        </div>
      )}
    </div>
  );
}
